import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";

const Terminal = forwardRef(({ onCommand, mining }, ref) => {  // Added mining as a prop
    const [lines, setLines] = useState([
        // `        SSSSS    OOOOO   L       M     M  III  N   N  EEEEE  RRRRR  
        // S        O   O   L       MM   MM   I   NN  N  E      R    R 
        // SSSSS    O   O   L       M M M M   I   N N N  EEEE   RRRRR  
        //     S    O   O   L       M  M  M   I   N  NN  E      R  R   
        // SSSSS    OOOOO   LLLLL   M     M  III  N   N  EEEEE  R   RR  
        // `,
        "Welcome to Solana AI Agent v1.0 🚀", 
        "Ready to get started? Type 'connect' to link your wallet and activate mining powers! ⚡",
        "Available commands: connect, start, stop, balance, withdraw, help, support"
    ]);
      
    const [input, setInput] = useState("");
    const [commandHistory, setCommandHistory] = useState([]); // Command history
    const [historyIndex, setHistoryIndex] = useState(-1); // Tracks current position in history
    const terminalEndRef = useRef(null);
    const inputRef = useRef(null); // Reference for the input field

    const commands = ["connect", "start", "stop", "balance", "withdraw", "help", "support"]; // List of available commands

    useImperativeHandle(ref, () => ({
        focusInput: () => {
            inputRef.current?.focus(); // Expose the focusInput method
        },
    }));

    useEffect(() => {
        let miningInterval;

        // Simulate fake mining process when mining starts
        if (mining) {
            miningInterval = setInterval(() => {
                addMiningLine(); // Add a new fake mining line every 500ms
            }, 500);
        }

        // Cleanup the interval when mining stops
        return () => {
            if (miningInterval) {
                clearInterval(miningInterval);
            }
        };
    }, [mining]); // Only run when 'mining' state changes

    useEffect(() => {
        // Scroll to the bottom when a new line is added
        terminalEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [lines]);

    const addLine = (text) => {
        setLines((prev) => [...prev, text]);
    };

    const addMiningLine = () => {
        const hash = Math.random().toString(36).substring(2, 15);
        const speed = (Math.random() * 60).toFixed(2);
        addLine(`Mining mempool ${hash} at ${speed} H/s...`);
    };

    const handleKeyDown = async (e) => {
        if (e.key === "Enter") {
            addLine(`> ${input}`);
            if (input.trim() !== "") {
                // Save to command history
                setCommandHistory((prevHistory) => [...prevHistory, input]);
                setHistoryIndex(-1); // Reset history index after new command
                const response = await onCommand(input);
                if (response) addLine(response);
            }
            setInput("");
        }

        // Handle Arrow Up (previous command)
        if (e.key === "ArrowUp") {
            if (historyIndex === -1) {
                setHistoryIndex(commandHistory.length - 1); // Start from the last command
            } else if (historyIndex > 0) {
                setHistoryIndex(historyIndex - 1); // Go to previous command
            }
            setInput(commandHistory[historyIndex] || "");
        }

        // Handle Arrow Down (next command)
        if (e.key === "ArrowDown") {
            if (historyIndex < commandHistory.length - 1) {
                setHistoryIndex(historyIndex + 1); // Go to next command
            } else {
                setHistoryIndex(-1); // Reset to no history
            }
            setInput(commandHistory[historyIndex] || "");
        }

        // Handle Tab for auto-completion
        if (e.key === "Tab") {
            e.preventDefault(); // Prevent the default Tab behavior (focus change)
            const matchedCommands = commands.filter((cmd) =>
                cmd.startsWith(input.trim()) // Filter commands that start with the input
            );
            if (matchedCommands.length === 1) {
                setInput(matchedCommands[0]); // Auto-complete with the matched command
            } else if (matchedCommands.length > 1) {
                // If multiple commands match, show suggestions
                addLine(`Available commands: ${matchedCommands.join(", ")}`);
            }
        }
    };

    return (
        <div className="terminal-container">
            <div className="terminal-output">
                {lines.map((line, index) => (
                    <div key={index} className="terminal-line">
                        {line.split("\n").map((subLine, subIndex) => (
                            <p key={subIndex} style={{ margin: 0 }}>
                                {subLine}
                            </p>
                        ))}
                    </div>
                ))}
                <div ref={terminalEndRef} />
            </div>
            <div className="terminal-input">
                <span className="prompt">CMD: </span> {/* Prefix added here */}
                <input
                    ref={inputRef} // Assign ref to input field
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoFocus
                />
            </div>

            <style jsx>{`
                .terminal-container {
                    background-color: black;
                    color: rgb(247, 103, 255);
                    font-family: "Courier New", Courier, monospace;
                    padding: 10px;
                    height: 100vh;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }
                .terminal-output {
                    flex-grow: 1;
                    overflow-y: auto;
                    margin-bottom: 10px;
                }
                .terminal-line {
                    line-height: 1.4;
                    word-break: break-word;
                    white-space: pre-wrap;
                }
                .terminal-input {
                    display: flex;
                    align-items: center;
                }
                .prompt {
                    margin-right: 5px;
                    color: rgb(255, 255, 255);
                }
                input {
                    background: transparent;
                    border: none;
                    color: rgb(255, 255, 255);
                    outline: none;
                    width: 100%;
                    font-family: inherit;
                    font-size: 16px;
                }
            `}</style>
        </div>
    );
});

export default Terminal;
